// front/src/utils/debug.js
import { CONFIG } from '../config/config';

const isBrowser = typeof window !== 'undefined';

export const debugEnabled = () =>
  process.env.REACT_APP_DEBUG === '1' ||
  (isBrowser && (window.__MUSIQ_DEBUG__ === true || window.__MUSIQ_DEBUG_SALLES__ === true));

const remoteLogEnabled = () =>
  // Active par défaut ; mets REACT_APP_REMOTE_LOG=0 pour couper si besoin
  process.env.REACT_APP_REMOTE_LOG !== '0';

/* ---------- utils sérialisation sûre & compacte ---------- */
const pretty = (body) => {
  try {
    if (typeof body === 'string') return JSON.parse(body);
    if (body && typeof body === 'object') return body;
  } catch {}
  return body;
};

function safeSer(v) {
  try {
    if (v == null) return v;
    if (typeof v === 'string') return v.length > 1000 ? v.slice(0, 1000) + '…' : v;
    // coupe les grosses chaînes dans les objets
    return JSON.parse(
      JSON.stringify(v, (_, val) =>
        typeof val === 'string' && val.length > 1000 ? val.slice(0, 1000) + '…' : val,
      ),
    );
  } catch {
    try {
      const s = String(v);
      return s.length > 1000 ? s.slice(0, 1000) + '…' : s;
    } catch {
      return '[unserializable]';
    }
  }
}

function truncateJson(obj, max = 1000) {
  try {
    const s = JSON.stringify(obj);
    return s.length > max ? s.slice(0, max) + '…' : s;
  } catch {
    return '[unserializable]';
  }
}

/* ---------- envoi au back pour figurer dans docker logs ---------- */
async function postClientLog({ level = 'info', ns = 'DBG', msg = '', extra = {} }) {
  if (!remoteLogEnabled()) return;
  try {
    const token =
      (isBrowser && (sessionStorage.getItem('token') || localStorage.getItem('token'))) || '';
    await fetch(`${CONFIG.API_URL}/dev/client-log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        level,
        msg: `[${ns}] ${msg}`,
        extra: {
          ...extra,
          href: isBrowser ? window.location.href : null,
          ts: Date.now(),
        },
      }),
      keepalive: true, // utile si navigation/fermeture onglet
    });
  } catch {
    /* no-op */
  }
}

/* ---------- API COMPAT : mklog(ns) -> (...args) ---------- */
export const mklog =
  (ns = 'DBG') =>
  (...args) => {
    // 1) console locale (si debug actif)
    if (debugEnabled()) {
      // eslint-disable-next-line no-console
      console.log(`[${ns}]`, ...args);
    }
    // 2) miroir serveur (toujours, sauf si REACT_APP_REMOTE_LOG=0)
    try {
      let msg = '';
      let extra = {};
      if (args.length) {
        msg = typeof args[0] === 'string' ? args[0] : truncateJson(args[0]);
        if (args.length > 1) {
          extra = { a1: safeSer(args[1]), a2: safeSer(args[2]), a3: safeSer(args[3]) };
        }
      }
      postClientLog({ ns, msg, extra });
    } catch {
      /* no-op */
    }
  };

/* ---------- fetch verbeux + miroir serveur ---------- */
/**
 * Usage :
 *   await dbgFetch('WIZ', url, { method:'PATCH', headers:{...}, body: JSON.stringify({...}) })
 */
export async function dbgFetch(ns, input, init = {}) {
  const log = mklog(ns);
  const method = (init?.method || 'GET').toUpperCase();
  const body = init?.body;

  // Request
  log('HTTP', method, input, body ? { body: pretty(body) } : undefined);
  postClientLog({
    ns,
    msg: `HTTP ${method} ${input}`,
    extra: body ? { body: pretty(body) } : {},
  });

  const res = await fetch(input, init);

  // Response (statut & CT)
  const ct = res.headers.get('content-type') || '';
  log('RES', { ok: res.ok, status: res.status, 'content-type': ct });
  postClientLog({
    ns,
    msg: `RES ${method} ${input}`,
    extra: { ok: res.ok, status: res.status, ct },
  });

  // Payload (avec coupe)
  try {
    const clone = res.clone();
    if (ct.includes('application/json')) {
      const j = await clone.json();
      log('RES JSON', j);
      postClientLog({ ns, msg: 'RES JSON', extra: { preview: truncateJson(j) } });
    } else {
      const t = await clone.text();
      const preview = String(t).slice(0, 500);
      log('RES TEXT', preview);
      postClientLog({ ns, msg: 'RES TEXT', extra: { preview } });
    }
  } catch (e) {
    log('RES read error', e?.message);
    postClientLog({
      ns,
      msg: 'RES read error',
      extra: { err: e?.message || String(e) },
      level: 'error',
    });
  }

  return res;
}
